import type { CoachImageAttachment } from './stream'

/**
 * Match the iOS attachment helper:
 *  - Cap at 5 attachments per message (backend max)
 *  - Cap at 5MB raw bytes each (~6.7MB base64 — backend caps at 7MB)
 *  - Sniff media type from magic bytes; reject unsupported
 *
 * Reading a File as base64: FileReader returns a data URL we strip
 * the prefix off. ArrayBuffer + manual base64 would avoid the copy
 * but FileReader is plenty fast for the size cap.
 */

export const MAX_ATTACHMENTS = 5
export const MAX_BYTES = 5_000_000

const MEDIA_BY_MAGIC: Array<{ test: (b: Uint8Array) => boolean; type: CoachImageAttachment['mediaType'] }> = [
  { test: (b) => b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff, type: 'image/jpeg' },
  { test: (b) => b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47, type: 'image/png' },
  { test: (b) => b[0] === 0x47 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x38, type: 'image/gif' },
  {
    test: (b) =>
      b[0] === 0x52 && b[1] === 0x49 && b[2] === 0x46 && b[3] === 0x46 &&
      b[8] === 0x57 && b[9] === 0x45 && b[10] === 0x42 && b[11] === 0x50,
    type: 'image/webp',
  },
]

export type AttachmentDraft = {
  id: string
  file: File
  previewUrl: string
  mediaType: CoachImageAttachment['mediaType']
  dataBase64: string
}

export async function makeAttachment(file: File): Promise<AttachmentDraft> {
  if (file.size > MAX_BYTES) {
    throw new Error('Image too large — keep attachments under 5 MB each.')
  }
  const buffer = await file.arrayBuffer()
  const bytes = new Uint8Array(buffer.slice(0, 12))
  const mediaType = MEDIA_BY_MAGIC.find((m) => m.test(bytes))?.type
  if (!mediaType) {
    throw new Error('Unsupported image format. JPEG, PNG, GIF, or WebP only.')
  }
  const dataBase64 = await fileToBase64(file)
  return {
    id: crypto.randomUUID(),
    file,
    previewUrl: URL.createObjectURL(file),
    mediaType,
    dataBase64,
  }
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'))
    reader.onload = () => {
      const result = reader.result
      if (typeof result !== 'string') {
        reject(new Error('Unexpected FileReader output'))
        return
      }
      // Strip the `data:image/png;base64,` prefix.
      const comma = result.indexOf(',')
      resolve(comma >= 0 ? result.slice(comma + 1) : result)
    }
    reader.readAsDataURL(file)
  })
}
